import React, { useEffect, useState } from "react";
import { useGoogleDriveApi } from "./googleDriveApi";
import { IAttachment } from "../interfaces/Booking.interface";
import { Button, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Typography, Box } from "@mui/material";
import { Role } from '../interfaces/constants';
import VideoPlayer from "./VideoPlayer";
import { PlayArrow, Download, Delete } from "@mui/icons-material";

interface UnifiedFileViewerProps {
  bookingId: string;
  reload: number;
  userRole?: Role;
}

export default function UnifiedFileViewer({ bookingId, reload, userRole }: UnifiedFileViewerProps) {
  const { getFilesByBookingId, getVideosByBookingId, deleteAttachment } = useGoogleDriveApi();
  const [files, setFiles] = useState<IAttachment[]>([]);
  const [currentVideo, setCurrentVideo] = useState<IAttachment | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");

  useEffect(() => {
    if (!bookingId) {
      setFiles([]);
      setCurrentVideo(null);
      return;
    }
    setStatus("loading");

    // Fetch both videos and files, then combine them
    Promise.all([
      getVideosByBookingId(bookingId),
      getFilesByBookingId(bookingId)
    ])
      .then(([videos, files]) => {
        // Combine both arrays and remove duplicates
        const allFiles = [...videos, ...files];
        const unique = Array.from(new Map(allFiles.map(f => [f.driveId, f])).values());
        setFiles(unique);
        
        // Set the first video as current if available
        const firstVideo = unique.find(f => f.mimeType.startsWith('video/'));
        setCurrentVideo(firstVideo || null);
        setStatus("idle");
      })
      .catch(() => setStatus("error"));
  }, [bookingId, getFilesByBookingId, getVideosByBookingId, reload]);

  const handleDelete = async (file: IAttachment) => {
    if (!window.confirm(`Are you sure to delete "${file.name}"? This operation cannot be undone.`)) return;
    try {
      await deleteAttachment(bookingId, file.driveId);
      
      // Refresh both videos and files
      Promise.all([
        getVideosByBookingId(bookingId),
        getFilesByBookingId(bookingId)
      ]).then(([videos, files]) => {
        const allFiles = [...videos, ...files];
        const unique = Array.from(new Map(allFiles.map(f => [f.driveId, f])).values());
        setFiles(unique);
        
        // Update current video if it was deleted
        if (currentVideo?.driveId === file.driveId) {
          const nextVideo = unique.find(f => f.mimeType.startsWith('video/'));
          setCurrentVideo(nextVideo || null);
        }
      });
    } catch (err) {
      console.error("Delete Fails", err);
      alert("Delete Fails: " + err);
    }
  };

  const handlePreviewVideo = (file: IAttachment) => {
    setCurrentVideo(file);
  };

  const getFileIcon = (mimeType: string) => {
    // Only videos can be previewed, all other files are downloads
    if (mimeType.startsWith('video/')) return <PlayArrow />;
    return <Download />;
  };


  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (status === "loading") return <div>Loading...</div>;
  if (status === "error") return <div style={{ color: "crimson" }}>Loading Failed</div>;
  if (!files.length) return <div style={{ color: "#666" }}>No Available Files</div>;

  const videoFiles = files.filter(f => f.mimeType.startsWith('video/'));
  const otherFiles = files.filter(f => !f.mimeType.startsWith('video/'));

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 2,
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      {/* Video Player Section */}
      {currentVideo && (
        <Box sx={{ 
          border: '1px solid #e0e0e0', 
          borderRadius: 2, 
          overflow: 'hidden',
          backgroundColor: '#fafafa',
          width: '100%',
          maxWidth: '100%'
        }}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: '#f5f5f5', 
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                minWidth: 0
              }}
              title={currentVideo.name}
            >
              {currentVideo.name}
            </Typography>
          </Box>
          <Box sx={{ p: 2, width: '100%', maxWidth: '100%' }}>
            <VideoPlayer 
              fileId={currentVideo.driveId} 
              filename={currentVideo.name}
            />
          </Box>
        </Box>
      )}

      {/* Files List */}
      <Box sx={{ width: '100%', maxWidth: '100%' }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Files ({files.length})
        </Typography>
        
        <List sx={{ 
          border: '1px solid #e0e0e0', 
          borderRadius: 2, 
          backgroundColor: '#fafafa',
          maxHeight: 400,
          overflow: 'auto',
          width: '100%',
          maxWidth: '100%'
        }}>
          {files.map((file) => (
            <ListItem 
              key={file.driveId}
              sx={{ 
                borderBottom: '1px solid #e0e0e0',
                '&:last-child': { borderBottom: 'none' },
                '&:hover': { backgroundColor: '#f5f5f5' },
                width: '100%',
                maxWidth: '100%',
                pr: 1
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mr: 2,
                flexShrink: 0
              }}>
                {getFileIcon(file.mimeType)}
              </Box>
              
              <ListItemText
                sx={{ 
                  minWidth: 0,
                  flex: 1,
                  overflow: 'hidden'
                }}
                primary={
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      minWidth: 0
                    }}
                    title={file.name}
                  >
                    {file.name}
                  </Typography>
                }
                secondary={
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 2, 
                    mt: 0.5,
                    flexWrap: 'wrap'
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(file.uploadedAt).toLocaleDateString()}
                    </Typography>
                    {file.size && (
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(file.size)}
                      </Typography>
                    )}
                  </Box>
                }
              />
              
              <ListItemSecondaryAction sx={{ 
                flexShrink: 0,
                display: 'flex',
                gap: 0.5
              }}>
                {/* Video Preview Button */}
                {file.mimeType.startsWith('video/') && (
                  <IconButton
                    onClick={() => handlePreviewVideo(file)}
                    color="primary"
                    size="small"
                    title="Preview Video"
                    sx={{ minWidth: 'auto' }}
                  >
                    <PlayArrow />
                  </IconButton>
                )}
                
                {/* Download Button for non-video files */}
                {!file.mimeType.startsWith('video/') && (
                  <IconButton
                    href={file.webContentLink ?? ""}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                    size="small"
                    title="Download File"
                    sx={{ minWidth: 'auto' }}
                  >
                    <Download />
                  </IconButton>
                )}
                
                {/* Delete Button (Admin/Teacher only) */}
                {(userRole === Role.Admin || userRole === Role.Teacher) && (
                  <IconButton
                    onClick={() => handleDelete(file)}
                    color="error"
                    size="small"
                    title="Delete File"
                    sx={{ minWidth: 'auto' }}
                  >
                    <Delete />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );
}
